import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, retry, timeout } from 'rxjs';
import { map } from 'rxjs/operators';
import { Company } from '../../models/company.model';
import { UrlReferenceService } from './url-reference.service';

export interface ApplicationData {
  companyName: string | null;
  employeeCount: number;
  dependentCount: number;
  maleCount: number;
  femaleCount: number;
  selectedQuote: string | null;
  completedSteps: number[];
  companyData?: any;
  companyDetails?: Company | null;
  policyStartDate?: string | null;
  entity700Number?: string | null;
  sponsorID?: number | null;
}

@Injectable({ providedIn: 'root' })
export class CompanyService {
  private companyDataSubject = new BehaviorSubject<any>(null);
  companyData$ = this.companyDataSubject.asObservable();

  private crNumberSubject = new BehaviorSubject<any>(null);
  crNumber$ = this.crNumberSubject.asObservable();

  private companyDataResponseSubject = new BehaviorSubject<any>(null);
  companyDataResponse$ = this.companyDataResponseSubject.asObservable();
  private readonly STORAGE_KEY = 'application_data';

  // Holds the currently selected companyId
  private currentSelectedCompanySubject = new BehaviorSubject<number | 0>(
    this.getInitialCompany()
  );
  currentSelectedCompany$ = this.currentSelectedCompanySubject.asObservable();

  private applicationDataSubject = new BehaviorSubject<ApplicationData>({
    companyName: null,
    employeeCount: 0,
    dependentCount: 0,
    maleCount: 0,
    femaleCount: 0,
    selectedQuote: null,
    completedSteps: [],
    companyData: null,
    companyDetails: null,
    policyStartDate: null,
    entity700Number: null,
    sponsorID: null,
  });

  public applicationData$: Observable<ApplicationData> =
    this.applicationDataSubject.asObservable();

  /** Get companyId from localStorage (if exists) */
  private getInitialCompany(): number | 0 {
    const companyId = localStorage.getItem('currentSelectedCompany');
    return companyId ? Number(companyId) : 0;
  }

  /** Set new companyId */
  setSelectedCompany(companyId: number): void {
    localStorage.setItem('currentSelectedCompany', companyId.toString());
    this.currentSelectedCompanySubject.next(companyId);
  }

  /** Get current companyId synchronously */
  getSelectedCompany(): number | 0 {
    return this.currentSelectedCompanySubject.value;
  }

  /** Clear selected company */
  clearSelectedCompany(): void {
    localStorage.removeItem('currentSelectedCompany');
    this.currentSelectedCompanySubject.next(0);
  }

  setCrNumber(crNumber: string) {
    localStorage.setItem('crNumber', crNumber);
    this.crNumberSubject.next({ crNumber });
    console.log('CR Number set:', this.crNumberSubject.value?.crNumber);
  }

  getCrNumber() {
    if (!this.crNumberSubject.value) {
      const storedCrNumber = localStorage.getItem('crNumber');
      if (storedCrNumber) {
        this.crNumberSubject.next({ crNumber: storedCrNumber });
      }
    }
    return this.crNumberSubject.value?.crNumber || '';
  }

  setCompanyDetails(company: Company) {
    localStorage.setItem('company', JSON.stringify(company));
    localStorage.setItem('companyId', JSON.stringify(company.id));
    this.companyDataSubject.next(company);
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      companyDetails: company,
    });
  }

  getCompanyDetails(): Company | null {
    const company = localStorage.getItem('company');
    if (company) {
      this.companyDataSubject.next(JSON.parse(company));
      return JSON.parse(company);
    }
    return null;
  }

  getCompanyId() {
    if (!this.companyDataSubject.value) {
      const storedCompanyData = localStorage.getItem('companyId');
      if (storedCompanyData) {
        this.companyDataSubject.next(JSON.parse(storedCompanyData));
      }
    }
    return (
      this.companyDataSubject.value?.id || localStorage.getItem('companyId')
    );
  }
  setCompanyData(data: any) {
    this.companyDataSubject.next(data);
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      companyData: data,
      entity700Number: data.entity700Number || currentData.entity700Number,
      sponsorID: data.sponsorID || currentData.sponsorID,
    });
  }

  getCompanyData() {
    return this.companyDataSubject.value;
  }

  setCompanyDataResponse(data: any) {
    this.companyDataResponseSubject.next(data);
  }

  getCompanyDataResponse() {
    return this.companyDataResponseSubject.value;
  }

  clearCompanyData() {
    this.companyDataSubject.next(null);
    this.companyDataResponseSubject.next(null);
  }

  constructor(private http: HttpClient, private urlRef: UrlReferenceService) {
    this.applicationData$.subscribe((data) => {
      this.saveToStorage(data);
    });
  }

  createCompany(
    companyData: Partial<Company>,
    userId: string
  ): Observable<Company> {
    return this.http.post<Company>(
      this.urlRef.saveCompanyUrl + `?userId=` + userId,
      companyData
    );
  }

  updateCompany(
    companyId: string | number,
    companyData: Partial<Company>,
    userId: string
  ): Observable<Company> {
    const url = `${this.urlRef.updateCompanyUrl}/${companyId}?userId=${userId}`;
    console.log('CompanyService: Updating company with URL:', url);
    console.log('CompanyService: Update data:', companyData);
    console.log('CompanyService: Company ID:', companyId);
    console.log('CompanyService: User ID:', userId);

    return this.http.put<Company>(url, companyData).pipe(
      timeout(30000), // 30 second timeout
      retry({
        count: 2,
        delay: 1000, // 1 second delay between retries
        resetOnSuccess: true,
      })
    );
  }

  saveCompanyDetails(
    companyData: Partial<Company>,
    userId: string
  ): Observable<Company> {
    return this.http.post<Company>(
      this.urlRef.saveCompanyDetailsUrl + `?userId=` + userId,
      companyData
    );
  }

  getCompanyByCrNumber(crNumber: string): Observable<Company> {
    return this.http.get<Company>(
      this.urlRef.getUserCompaniesUrl + `/company/${crNumber}`
    );
  }

  getCompanyByCompanyId(companyId: string): Observable<Company> {
    return this.http.get<Company>(
      this.urlRef.getUserCompaniesUrl + `/company/${companyId}`
    );
  }

  getCompanyById(companyId: string | number): Observable<Company> {
    return this.http.get<Company>(
      `${this.urlRef.currentAuthUrl}/companies/company/${companyId}`
    );
  }

  getUserCompanies(userId: string): Observable<Company[]> {
    return this.http.get<Company[]>(`${this.urlRef}/user/${userId}`);
  }

  getCompaniesByCompanyId(userId: string): Observable<Company[]> {
    return this.http.get<Company[]>(
      `${this.urlRef.getUserCompaniesUrl}/company/${userId}`
    );
  }

  verifyCrNumber(crNumber: string, companyId: string): Observable<any> {
    const url = this.urlRef.verifyCompanyCrUrl;
    const body = { crNumber, companyId };

    return this.http.post(url, body, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  getCompaniesByUserIdOrUserEmail(
    userId: string,
    userEmail: any
  ): Observable<Company[]> {
    return this.http.get<any[]>(
      `${this.urlRef.currentAuthUrl}/companies/${userId}/my-companies` +
        (userEmail ? `?userEmail=${userEmail}` : '')
    ).pipe(
      map((response: any[]) => {
        console.log('Raw API response:', response);
        // Handle different response structures
        if (Array.isArray(response)) {
          return response.map(company => this.normalizeCompanyData(company));
        }
        return [];
      })
    );
  }

  getCompaniesByUserId(userId: string): Observable<Company[]> {
    return this.http.get<Company[]>(
      `${this.urlRef.currentAuthUrl}/companies/${userId}/my-companies`
    );
  }

  assignUserToCompany(companyId: any, userId: string): Observable<any> {
    const url = `${this.urlRef.currentAuthUrl}/companies/assign-user?userId=${userId}&companyId=${companyId}`;
    return this.http.post(url, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  private loadFromStorage(): ApplicationData {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading from storage:', error);
    }

    return {
      companyName: null,
      employeeCount: 0,
      dependentCount: 0,
      maleCount: 0,
      femaleCount: 0,
      selectedQuote: null,
      completedSteps: [],
      companyData: null,
      companyDetails: null,
      policyStartDate: null,
      entity700Number: null,
      sponsorID: null,
    };
  }

  // Save data to localStorage
  private saveToStorage(data: ApplicationData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to storage:', error);
    }
  }

  // Get current application data
  getApplicationData(): ApplicationData {
    return this.applicationDataSubject.value;
  }

  // Update company name
  setCompanyName(name: string): void {
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      companyName: name,
    });
  }

  setEmployeeCount(count: number): void {
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      employeeCount: count,
    });
  }

  setDependentCount(count: number): void {
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      dependentCount: count,
    });
  }

  setMaleCount(count: number): void {
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      maleCount: count,
    });
  }

  setFemaleCount(count: number): void {
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      femaleCount: count,
    });
  }


  setSelectedQuote(quote: string): void {
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      selectedQuote: quote,
    });
  }

  setEntity700Number(entity700Number: string): void {
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      entity700Number: entity700Number,
    });
  }

  setSponsorID(sponsorID: number): void {
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      sponsorID: sponsorID,
    });
  }

  markStepCompleted(stepNumber: number): void {
    const currentData = this.applicationDataSubject.value;
    if (!currentData.completedSteps.includes(stepNumber)) {
      this.applicationDataSubject.next({
        ...currentData,
        completedSteps: [...currentData.completedSteps, stepNumber],
      });
    }
  }

  markStepIncomplete(stepNumber: number): void {
    const currentData = this.applicationDataSubject.value;
    this.applicationDataSubject.next({
      ...currentData,
      completedSteps: currentData.completedSteps.filter(
        (step) => step !== stepNumber
      ),
    });
  }

  // Calculate progress percentage (20% per step)
  getProgress(): number {
    const completedSteps =
      this.applicationDataSubject.value.completedSteps.length;
    return completedSteps * 20;
  }

  // Reset all data
  resetApplicationData(): void {
    const defaultData: ApplicationData = {
      companyName: null,
      employeeCount: 0,
      dependentCount: 0,
      maleCount: 0,
      femaleCount: 0,
      selectedQuote: null,
      completedSteps: [],
      companyData: null,
      companyDetails: null,
      policyStartDate: null,
      entity700Number: null,
      sponsorID: null,
    };

    this.applicationDataSubject.next(defaultData);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Clear storage (useful for logout)
  clearStorage(): void {
    this.resetApplicationData();
  }

  // Normalize company data to handle different field name formats
  private normalizeCompanyData(company: any): Company {
    console.log('Normalizing company data:', company);
    
    return {
      id: company.id || company.company_id,
      companyCode: company.companyCode || company.company_code,
      name: company.name || company.company_name,
      companyNameArabic: company.companyNameArabic || company.company_name_arabic,
      crNumber: company.crNumber || company.cr_number,
      sponsorID: company.sponsorID || company.sponsor_id,
      vatNumber: company.vatNumber || company.vat_number,
      contactEmail: company.contactEmail || company.contact_email,
      contactPhone: company.contactPhone || company.contact_phone,
      phoneNumber: company.phoneNumber || company.phone_number,
      bankNameID: company.bankNameID || company.bank_name_id,
      ibanNumber: company.ibanNumber || company.iban_number,
      policyStartDate: company.policyStartDate || company.policy_start_date,
      numberOfEmployees: company.numberOfEmployees || company.number_of_employees,
      entity700Number: company.entity700Number || company.entity_700_number,
      status: company.status,
    };
  }
}
